/* eslint-disable camelcase */
import { get } from 'lodash';
import {
  all, call, put, select,
} from 'redux-saga/effects';
import center from '@turf/center';
import * as turf from '@turf/helpers';
import queryString from 'query-string';
import { API } from '../../../api';
import { ALLOWED_PROP_TYPES } from './constants';
import { fetchParcelSales } from './fetch-sales';
import { fetchWalkscore } from './fetch-walkscore';
import { fetchDataStreams } from './fetch-data-streams';
import { fetchSingleParcelGraphs } from './fetch-single-parcel-graphs';
import {
  getReportResponses,
  getServerConfig,
  getFallbackReport,
} from './selectors';
import { fetchParcelRelatedRecords } from './fetch-parcel-related-records';

const getGeometry = (bboxResult) => {
  const parsedBbox = bboxResult.bboxes[0].map(([ln, la]) => [
    parseFloat(ln),
    parseFloat(la),
  ]);
  const features = turf.featureCollection(
    parsedBbox.map((latLng) => turf.point(latLng)),
  );
  const point = center(features);
  return {
    parsedBbox,
    point: point.geometry.coordinates,
  };
};

const getMapHoverFilters = ({ mapHoverFilters }) => mapHoverFilters;
const makeKey = ({ column, source }) => (source ? `${source}:${column}` : column);
const filterProperties = ({ type, endpoint, input }) => {
  if (!ALLOWED_PROP_TYPES.includes(type)) return false;
  return !endpoint && input == null;
};

const getDataStreamConfigs = (config) => config.report.sections[0].properties.filter(
  ({ type }) => type === 'DATA_STREAM',
);

export function* fetchParcelByCoords(action) {
  yield put({ type: 'START_REPORT_LOADING' });


  const config = get(action, 'payload.config', {});
  const { report } = config;

  const serverConfig = yield select(getServerConfig);
  const { lat, lng } = config.lngLat;
  const mapHoverFilters = yield select(getMapHoverFilters);

  const query = {
    ...mapHoverFilters,
    latlng: `${lat},${lng}`,
    props: config.report.sections
      .reduce((acc, section) => {
        const columns = section.properties
          .filter(filterProperties)
          .map(makeKey);
        return acc.concat(columns);
      }, [])
      .filter(Boolean)
      .join(','),
  };

  if (config.report.source) query.source = config.report.source;

  const path = `/parcel/${serverConfig.namespace}/coord?${queryString.stringify(
    query,
  )}${report.filterString ? `&${report.filterString}` : ''
    }`;

  const results = yield call(API, 'get', 'api', path);

  const identifierKey = report.identifier || 'parcel_id';
  const identifier = get(results, `parcels[0].${identifierKey}`);

  const fallbackReport = yield select(getFallbackReport);

  if (!identifier && fallbackReport && !action.payload.isFallback) {
    const newAction = {
      ...action,
      payload: {
        ...action.payload,
        isFallback: true,
        config: {
          ...action.payload.config,
          report: fallbackReport,
        },
      },
    };

    yield call(fetchParcelByCoords, newAction);
    return;
  }

  if (!identifier) {
    yield put({ type: 'CLOSE_MODAL' });
    yield put({
      type: 'UPDATE_REPORT_CONTENT',
      payload: null,
    });
    yield put({
      type: 'UPDATE_SIDE_PANEL_CONTENT',
      payload: null,
    });
    yield put({ type: 'STOP_REPORT_LOADING' });

    return;
  }

  yield put({
    type: 'SELECT_PARCEL_REPORT',
    payload: identifier,
  });

  const bboxQuery = {
    [identifierKey]: identifier,
    props: identifierKey,
  };

  if (config.report.source) bboxQuery.source = config.report.source;

  const filterString = queryString.stringify(bboxQuery);

  const bboxPath = `/bbox/${serverConfig.namespace}?${filterString}&radius=radius%3A500`;

  const {
    bboxResult,
    saleInfo,
    dataStreams,
    neighborhoods,
    enforcements,
    certificates,
    permits,
    interventions,
  } = yield all({
    bboxResult: call(API, 'get', 'api', bboxPath),
    saleInfo: call(fetchParcelSales, results.parcels[0].parcel_id, config),
    dataStreams: call(
      fetchDataStreams,
      getDataStreamConfigs(config),
      results.parcels[0].parcel_id,
    ),
    neighborhoods: call(fetchParcelRelatedRecords, 'parcels_polygon', results.parcels[0].parcel_id, config),
    enforcements: call(fetchParcelRelatedRecords, 'enforcements', results.parcels[0].parcel_id, config),
    certificates: call(fetchParcelRelatedRecords, 'certificates', results.parcels[0].parcel_id, config),
    permits: call(fetchParcelRelatedRecords, 'permits', results.parcels[0].parcel_id, config),
    interventions: call(fetchParcelRelatedRecords, 'interventions', results.parcels[0].parcel_id, config),
  });

  const { point, parsedBbox } = getGeometry(bboxResult);

  const walkscoreInfo = yield call(
    fetchWalkscore,
    results.parcels[0].address,
    point[1],
    point[0],
    config,
  );

  const finalParcels = [
    {
      ...results.parcels[0],
      ...(saleInfo || {}),
      ...(neighborhoods || {}),
      ...(enforcements || {}),
      ...(certificates || {}),
      ...(permits || {}),
      ...(interventions || {}),
      ...(walkscoreInfo || {}),
      dataStreams,
    },
  ];

  const payload = {
    key: identifier,
    parcels: finalParcels,
    report: config.report,
    bbox: parsedBbox,
    graphs: [],
    point,
  };
  yield put({
    type: 'UPDATE_REPORT_CONTENT',
    payload,
  });
  yield put({
    type: 'UPDATE_SIDE_PANEL_CONTENT',
    payload,
  });

  yield put({
    type: 'ADD_PARCELS',
    payload: {
      identifierKey,
      parcels: results.parcels,
    },
  });
  yield put({ type: 'STOP_REPORT_LOADING' });
  yield call(fetchSingleParcelGraphs, {
    serverConfig,
    filterString,
    report,
    identifier,
    finalParcels,
    config,
    parsedBbox,
    point,
  });

  yield put({
    type: 'TRACK',
    payload: {
      name: 'Report opened',
      type: 'Single Parcel',
      identifier,
    },
  });
}

export function* fetchParcelById(action) {
  try {
    yield put({ type: 'START_REPORT_LOADING' });

    const config = get(action, 'payload.config', {});
    const { report } = config;

    const serverConfig = yield select(getServerConfig);
    const { identifier = 'parcel_id' } = config;
    const id = config[identifier];

    yield put({
      type: 'SELECT_PARCEL_REPORT',
      payload: id,
    });

    const reportResponses = yield select(getReportResponses);
    if (reportResponses && reportResponses[id]) {
      yield put({
        type: 'UPDATE_REPORT_CONTENT',
        payload: reportResponses[id],
      });
      yield put({
        type: 'UPDATE_SIDE_PANEL_CONTENT',
        payload: reportResponses[id],
      });
      yield put({ type: 'STOP_REPORT_LOADING' });
    }
    const mapHoverFilters = yield select(getMapHoverFilters);

    const query = {
      ...mapHoverFilters,
      props: config.report.sections
        .reduce((acc, section) => {
          const columns = section.properties
            .filter(filterProperties)
            .map(makeKey);
          return acc.concat(columns);
        }, [])
        .filter(Boolean)
        .join(','),
    };

    const filterString = queryString.stringify({
      ...mapHoverFilters,
      [identifier]: id,
      props: identifier,
      radius: 'radius:100',
    });
    const bboxPath = `/bbox/${serverConfig.namespace}?${filterString}`;
    const path = `/parcel/${serverConfig.namespace
      // eslint-disable-next-line camelcase
      }/${id}?${queryString.stringify(query)}${report.filterString ? `&${report.filterString}` : ''
      }`;

    const { bboxResult, results, dataStreams } = yield all({
      bboxResult: call(API, 'get', 'api', bboxPath),
      results: call(API, 'get', 'api', path),
      dataStreams: call(
        fetchDataStreams,
        getDataStreamConfigs(config),
        id,
      ),
    });

    const resultId = get(results, `parcels[0].${identifier}`);

    if (!resultId) {
      yield put({ type: 'CLOSE_MODAL' });
      return;
    }

    const { point, parsedBbox } = getGeometry(bboxResult);

    yield put({
      type: 'ADD_PARCELS',
      payload: {
        parcels: results.parcels,
        identifierKey: identifier,
      },
    });

    const {
      walkscoreInfo,
      saleInfo,
      censusTable,
      neighborhoods,
      enforcements,
      certificates,
      permits,
      interventions,
    } = yield all({
      walkscoreInfo: call(
        fetchWalkscore,
        results.parcels[0].address,
        point[1],
        point[0],
        config,
      ),
      saleInfo: call(fetchParcelSales, results.parcels[0][identifier], config),
      neighborhoods: call(fetchParcelRelatedRecords, 'parcels_polygon', results.parcels[0][identifier], config),
      enforcements: call(fetchParcelRelatedRecords, 'enforcements', results.parcels[0][identifier], config),
      certificates: call(fetchParcelRelatedRecords, 'certificates', results.parcels[0][identifier], config),
      permits: call(fetchParcelRelatedRecords, 'permits', results.parcels[0][identifier], config),
      interventions: call(fetchParcelRelatedRecords, 'interventions', results.parcels[0][identifier], config),
    });

    const finalParcels = [
      {
        ...results.parcels[0],
        ...(saleInfo || {}),
        ...(neighborhoods || {}),
        ...(enforcements || {}),
        ...(certificates || {}),
        ...(permits || {}),
        ...(interventions || {}),
        ...(walkscoreInfo || {}),
        dataStreams,
      },
    ];
    const payload = {
      parcels: finalParcels,
      key: id,
      report: config.report,
      bbox: parsedBbox,
      censusTable,
      point,
      graphs: [],
    };

    yield put({
      type: 'SET_MAP_SETTINGS',
      payload: {
        id: 'MAIN_MAP',
        settings: {
          zoom: 16,
          center: {
            lat: point[1],
            lng: point[0],
          },
        },
      },
    });

    yield put({
      type: 'UPDATE_REPORT_CONTENT',
      payload,
    });
    yield put({
      type: 'UPDATE_SIDE_PANEL_CONTENT',
      payload,
    });
    yield put({ type: 'STOP_REPORT_LOADING' });

    yield call(fetchSingleParcelGraphs, {
      serverConfig,
      filterString,
      report,
      identifier: id,
      finalParcels,
      config,
      parsedBbox,
      point,
    });

    yield put({
      type: 'TRACK',
      payload: {
        name: 'Report opened',
        type: 'Single Parcel',
        id,
        address: get(
          results,
          'parcels[0].add_combo',
          get(results, 'parcels[0].address'),
        ),
      },
    });
  } catch (e) {
    yield put({ type: 'STOP_REPORT_LOADING' });
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

export function* fetchParcel(action) {
  const config = get(action, 'payload.config', {});
  const { identifier } = config;
  const id = config[identifier || 'parcel_id'];
  if (id) {
    yield call(fetchParcelById, action);
    return;
  }
  yield call(fetchParcelByCoords, action);
}
